import { useState, useMemo } from 'react'
import { Alert, Button, Input, Space, Switch } from 'antd'
import { ReloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { AppLayout } from '../../../components/AppLayout'
import { useAnnouncements } from '../hooks/useAnnouncements'
import { AnnouncementsTable } from '../components/AnnouncementsTable'
import { CreateAnnouncementModal } from '../components/CreateAnnouncementModal'
import { EditAnnouncementModal } from '../components/EditAnnouncementModal'
import { Loading } from '../../../components/Loading'
import { EmptyState } from '../../../components/EmptyState'
import type { Announcement } from '../types/announcement.types'

export function AnnouncementsPage() {
  const [showInactive, setShowInactive] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)

  const { announcements, loading, error, refresh } = useAnnouncements({
    includeInactive: showInactive
  })

  const filteredAnnouncements = useMemo(() => {
    if (!searchTerm.trim()) return announcements

    const search = searchTerm.toLowerCase().trim()
    return announcements.filter(
      announcement =>
        announcement.code.toLowerCase().includes(search) ||
        announcement.message.toLowerCase().includes(search) ||
        (announcement.tenantCode?.toLowerCase().includes(search) ?? false)
    )
  }, [announcements, searchTerm])

  const handleEdit = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement)
    setEditModalOpen(true)
  }

  const handleEditClose = () => {
    setEditModalOpen(false)
    setSelectedAnnouncement(null)
  }

  if (error) {
    return (
      <AppLayout>
        <Alert
          message="Error Loading Announcements"
          description={error.message}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={refresh}>
              Retry
            </Button>
          }
        />
      </AppLayout>
    )
  }

  if (loading && announcements.length === 0) {
    return (
      <AppLayout>
        <Loading />
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)' }}>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '16px',
            flexShrink: 0
          }}
        >
          <Space size="middle" wrap>
            <Input
              placeholder="Search by code or message..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 350 }}
              allowClear
            />
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
              New Announcement
            </Button>
          </Space>
          <Space style={{ marginLeft: 'auto' }}>
            <span style={{ fontSize: '14px' }}>Show inactive:</span>
            <Switch checked={showInactive} onChange={setShowInactive} />
          </Space>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          {filteredAnnouncements.length === 0 && searchTerm ? (
            <EmptyState description={`No announcements found matching "${searchTerm}"`} />
          ) : filteredAnnouncements.length === 0 ? (
            <EmptyState description="No announcements found" />
          ) : (
            <AnnouncementsTable
              announcements={filteredAnnouncements}
              loading={loading}
              showInactive={showInactive}
              onEdit={handleEdit}
              onRefresh={refresh}
            />
          )}
        </div>
      </div>

      <CreateAnnouncementModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onSuccess={refresh} />

      <EditAnnouncementModal
        open={editModalOpen}
        announcement={selectedAnnouncement}
        onClose={handleEditClose}
        onSuccess={refresh}
      />
    </AppLayout>
  )
}
